import { Service } from '../contexts/booking/BookingContext';

/**
 * Gets the numeric price from a service, handling different data types.
 * Uses discountedPrice if available, otherwise uses regular price.
 * @param service The service object containing price information
 * @returns The price as a number
 */
export const getNumericPrice = (service: Service): number => {
  // If service has a discounted price, use that instead
  if (service.discountedPrice) {
    const discountedNum = parseFloat(service.discountedPrice.toString().replace(/[^0-9.]/g, ''));
    if (!isNaN(discountedNum) && discountedNum > 0) {
      return discountedNum;
    }
  }

  // Fall back to regular price
  if (typeof service.price === 'number') return service.price;
  if (typeof service.price === 'string') {
    const parsed = parseFloat(service.price.replace(/[^0-9.]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

/**
 * Gets the numeric duration from a service, handling different data types.
 * @param service The service object containing duration information
 * @returns The duration as a number in minutes
 */
export const getNumericDuration = (service: Service): number => {
  if (typeof service.duration === 'number') return service.duration;
  if (typeof service.duration === 'string') {
    const parsed = parseInt(service.duration, 10);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

/**
 * Calculates the total duration of selected services.
 * @param services Array of selected services
 * @returns Total duration in minutes
 */
export const getTotalDuration = (services: Service[]): number => {
  return services.reduce((total, service) => 
    total + getNumericDuration(service), 0);
};

/**
 * Calculates the total price of selected services.
 * @param services Array of selected services
 * @returns Total price
 */
export const getTotalPrice = (services: Service[]): number => {
  return services.reduce((total, service) => 
    total + getNumericPrice(service), 0);
};

/**
 * Formats a price for display with 2 decimal places
 * @param price The price to format
 * @returns Formatted price string
 */
export const formatPrice = (price: number): string => {
  return price.toFixed(2);
};

// =============================================================================
// DISCOUNT & WALLET CALCULATIONS
// =============================================================================

/**
 * Default member discount percentage (can be overridden from database)
 */
export const DEFAULT_MEMBER_DISCOUNT_PERCENT = 20;

/**
 * Pricing breakdown for booking display
 */
export interface PricingBreakdown {
  subtotal: number;              // Original service total
  memberDiscountPercent: number; // e.g., 10
  memberDiscountAmount: number;  // e.g., 10.00
  afterDiscount: number;         // subtotal - memberDiscountAmount
  walletBalance: number;         // User's wallet balance
  canPayWithWallet: boolean;     // wallet >= afterDiscount
  finalAmount: number;           // Amount to charge (afterDiscount if paying with card)
}

/**
 * Calculate full pricing breakdown including member discount
 * @param services Array of selected services
 * @param isMember Whether user has active membership
 * @param walletBalance User's wallet balance (default 0)
 * @param memberDiscountPercent Override discount percent (default 10)
 * @returns Full pricing breakdown
 */
export const calculatePricingBreakdown = (
  services: Service[],
  isMember: boolean = false,
  walletBalance: number = 0,
  memberDiscountPercent: number = DEFAULT_MEMBER_DISCOUNT_PERCENT
): PricingBreakdown => {
  const subtotal = getTotalPrice(services);

  // Calculate member discount
  const discountPercent = isMember ? memberDiscountPercent : 0;
  const memberDiscountAmount = isMember
    ? Math.round(subtotal * discountPercent) / 100
    : 0;

  // After discount amount
  const afterDiscount = Math.round((subtotal - memberDiscountAmount) * 100) / 100;

  // Check if wallet can cover full amount
  const canPayWithWallet = walletBalance >= afterDiscount;

  return {
    subtotal,
    memberDiscountPercent: discountPercent,
    memberDiscountAmount,
    afterDiscount,
    walletBalance,
    canPayWithWallet,
    finalAmount: afterDiscount,
  };
};

/**
 * Calculate wallet top-up bonus based on tiers
 * @param amount Top-up amount
 * @param tiers Array of bonus tiers from database
 * @returns Bonus percentage and amount
 */
export interface WalletBonusTier {
  min_amount: number;
  bonus_percent: number;
}

export interface WalletBonusResult {
  bonusPercent: number;
  bonusAmount: number;
  totalCredit: number;
}

export const calculateWalletBonus = (
  amount: number,
  tiers: WalletBonusTier[] = []
): WalletBonusResult => {
  // Sort tiers by min_amount descending to find highest applicable tier
  const sortedTiers = [...tiers].sort((a, b) => b.min_amount - a.min_amount);

  // Find first tier where amount >= min_amount
  const applicableTier = sortedTiers.find(tier => amount >= tier.min_amount);

  if (!applicableTier) {
    return {
      bonusPercent: 0,
      bonusAmount: 0,
      totalCredit: amount,
    };
  }

  const bonusAmount = Math.round(amount * applicableTier.bonus_percent) / 100;

  return {
    bonusPercent: applicableTier.bonus_percent,
    bonusAmount,
    totalCredit: amount + bonusAmount,
  };
};

/**
 * Validates if a service has the minimum required properties.
 * @param service The service to validate
 * @returns True if service is valid
 */
export const isValidService = (service: any): boolean => {
  return (
    service && 
    typeof service === 'object' &&
    'id' in service &&
    'name' in service
  );
};

/**
 * Normalizes a service object to ensure it has consistent properties.
 * @param service The service to normalize
 * @returns Normalized service object
 */
export const normalizeService = (service: any): Service => {
  const normalized: Service = {
    id: service.id,
    name: service.name,
    duration: service.duration || 0,
    price: service.price || 0,
    category_id: service.category_id || '',
  };

  // Preserve discountedPrice if it exists (promotional pricing)
  if (service.discountedPrice) {
    normalized.discountedPrice = service.discountedPrice;
  }

  // Ensure price and duration are stored as numbers
  // Note: getNumericPrice will use discountedPrice if available
  normalized.numericPrice = getNumericPrice(normalized);
  normalized.numericDuration = getNumericDuration(normalized);

  // Add any additional fields from the original service
  normalized.short_description = service.short_description;
  normalized.description = service.description;

  return normalized;
};
